/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
import {
	ConfigurableModel,
	ConfigurableTrim,
	ConfigurableVehicle,
} from '../models/CarConfigurator';

async function throwJatoResponseError(res: Response): Promise<never> {
	const raw = await res.text();
	let body: unknown = raw;
	try {
		body = raw ? JSON.parse(raw) : null;
	} catch {}

	const message =
		(body && typeof body === 'object' && 'message' in body
			? String((body as { message?: unknown }).message)
			: typeof body === 'string' && body
			? body
			: res.statusText) || `HTTP ${res.status}`;

	const error = new Error(message) as Error & {
		status?: number;
		statusText?: string;
		body?: unknown;
	};
	error.status = res.status;
	error.statusText = res.statusText;
	error.body = body;
	throw error;
}

const CONFIGURATOR_BASE_URL = (
	process.env.REACT_APP_CONFIGURATOR_BASE_URL ?? ''
).trim();

export class CarConfiguratorControllerService {
	constructor(public readonly httpRequest: BaseHttpRequest) {}

	public getVehicles(): CancelablePromise<Array<ConfigurableVehicle>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/configurables',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getMakes(): CancelablePromise<Array<string>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/configurables/makes',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getModels(make: string): CancelablePromise<Array<ConfigurableModel>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/configurables/models',
			query: { make },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getTrims(
		make: string,
		model: string
	): CancelablePromise<Array<ConfigurableTrim>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/configurables/versions',
			query: { make, model },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public updateConfigurable(
		id: string,
		configurable: boolean,
		manual_override?: boolean
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/vehicles/configurables/{id}',
			path: { id },
			query: { configurable, manual_override },
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public async getJatoOptionals(jatoId: number): Promise<any> {
		const { TOKEN } = this.httpRequest.config;
		const token = typeof TOKEN === 'function' ? await TOKEN({} as any) : TOKEN;

		const headers: Record<string, string> = {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		};

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const res = await fetch(
			`${CONFIGURATOR_BASE_URL}/jato/vehicles/${jatoId}/normalized`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwJatoResponseError(res);
	}

	public async validate(
		jatoId: number,
		payload: { market: string; selectedOptionIds: number[] }
	): Promise<any> {
		const { TOKEN } = this.httpRequest.config;
		const token = typeof TOKEN === 'function' ? await TOKEN({} as any) : TOKEN;

		const headers: Record<string, string> = {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		};

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const res = await fetch(
			`${CONFIGURATOR_BASE_URL}/jato/vehicles/${jatoId}/options/validate`,
			{ headers, method: 'POST', body: JSON.stringify(payload) }
		);

		if (res.ok) {
			return res.json();
		}

		return throwJatoResponseError(res);
	}
}
